import React, { useEffect, useMemo, useRef } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components/macro'
import * as Yup from 'yup'
import { Formik } from 'formik'
import axios from 'utils/axios'
import jwtDecode from 'jwt-decode'

import AlertTitle from '@mui/material/AlertTitle'
import MuiAlert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import MuiTextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import spacing from '@mui/system/spacing'

import useAuth from 'hooks/useAuth'
import useTheme from 'hooks/useTheme'

const Alert = styled(MuiAlert)(spacing)

const TextField = styled(MuiTextField)(spacing)

function LogIn({ is2FA, shortName }) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const formikRef = useRef(null)
  const { logIn, setInstance } = useAuth()
  const {
    texts: { login_help },
  } = useTheme()

  const handleTwoFactorLogin = async ({ username, password }) => {
    const res = await axios.post(`/user-sessions`, {
      username,
      password,
    })

    const { token } = res.data

    if (token) {
      setInstance(shortName, token)
    }

    const { UserPhoneMasked } = jwtDecode(token)

    navigate(`/auth/two-factor`, {
      state: {
        username,
        shortName,
        userPhone: UserPhoneMasked,
        responseData: res.data,
      },
    })
  }

  const handleSubmit = async (
    values,
    { setErrors, setStatus, setSubmitting }
  ) => {
    const { username, password, instanceId } = values
    try {
      if (is2FA) {
        await handleTwoFactorLogin({
          username,
          password,
        })
      } else await logIn({ username, password, instanceId })
    } catch (error) {
      const message =
        error.response?.data?.display_message ||
        'Please check your username and password and try again.'

      setStatus({ success: false })
      setErrors({ submit: message })
      setSubmitting(false)
    }
  }

  const queryError = useMemo(() => {
    const errorKeys = [...searchParams.keys()]
    const errors = []

    errorKeys.forEach((key) => {
      if (!key.includes('errors')) return

      const error = searchParams.get(key)
      if (error) errors.push(error.replace('_', ' '))
    })

    return errors.join('; ')
  }, [searchParams])

  useEffect(() => {
    if (formikRef?.current) {
      const { setValues, values } = formikRef.current
      setValues({
        ...values,
        instanceId: shortName,
      })
    }
  }, [shortName, formikRef])

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={{
        username: process.env.REACT_APP_USERNAME || '',
        password: process.env.REACT_APP_PASSWORD || '',
        twoFactorCode: '',
        instanceId: shortName,
        submit: false,
      }}
      validationSchema={Yup.object().shape({
        username: Yup.string().required('Username is required'),
        password: Yup.string().required('Password is required'),
        instanceId: Yup.string().required(
          'Instance shortname is required in URL'
        ),
      })}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
      }) => (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Typography mb={2}>Please Log In to continue</Typography>
          {errors.submit && (
            <Alert mt={2} mb={3} severity="warning">
              <AlertTitle>Unable to log in</AlertTitle>
              {errors.submit}
            </Alert>
          )}
          {queryError && (
            <Alert
              mt={2}
              mb={3}
              severity="warning"
              sx={{ textTransform: 'capitalize' }}
            >
              <AlertTitle>{queryError}</AlertTitle>
            </Alert>
          )}
          <TextField
            type="username"
            name="username"
            label="Username"
            value={values.username}
            error={Boolean(touched.username && errors.username)}
            fullWidth
            helperText={touched.username && errors.username}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
            disabled={!shortName}
          />
          <TextField
            type="password"
            name="password"
            label="Password"
            value={values.password}
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            onBlur={handleBlur}
            onChange={handleChange}
            my={2}
            disabled={!shortName}
          />
          <Box
            sx={{
              textAlign: 'center',
              color: 'error.main',
            }}
          >
            {errors.instanceId}
          </Box>
          <Stack spacing={4} sx={{ flex: 1 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Button
                LinkComponent={Link}
                to={`/auth/password-reset/${shortName}`}
                disabled={!shortName}
              >
                Forgot your password?
              </Button>
            </Box>
            <Box sx={{ width: '100%', display: 'flex' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ width: '50%', mx: 'auto' }}
                disabled={!shortName || isSubmitting}
              >
                Log In
              </Button>
            </Box>
          </Stack>
          <Box sx={{ width: '100%', display: 'flex', textAlign: 'center' }}>
            <Typography
              fontSize={12}
              color="darkgray.main"
              sx={{ width: '50%', mx: 'auto' }}
            >
              {login_help}
            </Typography>
          </Box>
        </Box>
      )}
    </Formik>
  )
}

export default LogIn
